import { useState } from "react";
import { Lock, Check, ChevronDown } from "lucide-react";

const PERKS = {
  100: {
    title: "Bronze",
    perks: [
      "Standard daily deposit and withdrawal caps",
      "Standard manual payout processing queue",
      "Full access to trading pairs and live market feeds",
    ],
  },
  250: {
    title: "Silver",
    perks: [
      "Early notification window on daily boost codes",
      "Reduced internal trading and conversion fees",
      "Priority chat and email support",
    ],
  },
  500: {
    title: "Gold",
    perks: [
      "Priority withdrawal queue — front of the line",
      "VIP market analysis and dealer signals",
      "Higher deposit and single-transaction ceilings",
    ],
  },
  1000: {
    title: "Black",
    perks: [
      "White-glove instant payout processing",
      "Zero platform fees on internal swaps and trades",
      "Direct concierge access for fast-track troubleshooting",
    ],
  },
  2000: {
    title: "Platinum",
    perks: [
      "Dedicated Platinum concierge line (SAST business hours)",
      "Higher simulated daily yield cap (1.5% / 90-day cycle)",
      "Priority booking for strategy roundtables",
    ],
  },
  3000: {
    title: "Sapphire",
    perks: [
      "Sapphire research briefings on macro signals",
      "Boosted withdrawal priority above Platinum",
      "Early access to new tier-exclusive instruments",
    ],
  },
  4000: {
    title: "Ruby",
    perks: [
      "Ruby fast-track payout window (same-business-day review)",
      "Named account manager and quarterly review call",
      "Discretionary cycle extensions on request",
    ],
  },
  5000: {
    title: "Diamond",
    perks: [
      "Diamond-tier executive concierge · 24/7 escalation",
      "Access to private co-investment simulation rooms",
      "Zero processing fees on cycle rollovers",
    ],
  },
  8000: {
    title: "Emerald",
    perks: [
      "Emerald hospitality — invite-only strategy summits",
      "120-day cycles with elevated yield bands",
      "White-glove onboarding for referred clients",
    ],
  },
  10000: {
    title: "Executive VIP",
    perks: [
      "Executive VIP steering group access",
      "Personalised cycle planning with the research desk",
      "Priority voice line to the operations director",
    ],
  },
  20000: {
    title: "Diplomatic",
    perks: [
      "Diplomatic flagship — bespoke 180-day cycles",
      "Direct line to founder-level advisory",
      "First-refusal access on every new programme",
    ],
  },
};

const TIERS = [100, 250, 500, 1000, 2000, 3000, 4000, 5000, 8000, 10000, 20000];

export default function TierPerks({ tier }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="border border-white/10 bg-surface" data-testid="tier-perks">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-6 py-4 text-left transition-colors hover:bg-white/[0.02]"
      >
        <span className="flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-[0.3em] text-gold">Perks & benefits</span>
          <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">
            {PERKS[tier]?.title || "Starter"} tier active
          </span>
        </span>
        <ChevronDown className={`h-4 w-4 text-white/50 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="grid grid-cols-1 gap-3 px-6 pb-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {TIERS.map((t) => {
            const active = tier >= t;
            const info = PERKS[t];
            return (
              <div
                key={t}
                data-testid={`tier-card-${t}`}
                className={`relative border p-4 transition-all ${
                  active ? "border-gold/40 bg-gold/[0.03]" : "border-white/10 bg-ink/40 opacity-55"
                }`}
              >
                <div className="mb-3 flex items-start justify-between gap-2">
                  <p className={`text-[11px] font-semibold uppercase leading-tight tracking-[0.18em] ${active ? "text-gold" : "text-white/40"}`}>
                    {info.title}
                  </p>
                  {active ? (
                    <span className="flex shrink-0 items-center gap-1 text-[9px] uppercase tracking-[0.2em] text-[#26A69A]">
                      <Check className="h-3 w-3" /> Unlocked
                    </span>
                  ) : (
                    <span className="flex shrink-0 items-center gap-1 text-[9px] uppercase tracking-[0.2em] text-white/35">
                      <Lock className="h-3 w-3" /> ${t}+
                    </span>
                  )}
                </div>
                <ul className="space-y-1.5 text-[11px] leading-relaxed text-white/70">
                  {info.perks.map((p) => (
                    <li key={p} className={`flex gap-1.5 ${active ? "" : "text-white/40"}`}>
                      <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-gold/60" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
